import { Add, Help, Remove } from "@mui/icons-material";
import { Box, Button, IconButton, Table } from "@mui/joy";
import { Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";

import { NumberInput } from "../forms/TextInput";
import { useDisplayGenericModal } from "../modals/useModal";

type ScoringConfig = {
  successPoints: number[];
  segment1Points?: number[];
  segment2Points?: number[];
};

// Compact row buttons: no minimum height or icon gap, with the browser's
// default button padding and font size.
const compactButtonSx = {
  minHeight: 0,
  paddingBlock: "1px",
  paddingInline: "6px",
  fontSize: "13.333px",
  "--Button-gap": "0px",
};

const fillToLength = <T,>(arr: T[], length: number) => {
  const filled = [...arr];
  while (filled.length < length) {
    filled.push(undefined as unknown as T);
  }
  return filled;
};

export default function CustomScoringTableInput({
  name,
  segmentsCount,
  smallInputs,
  hasFixedPenalty,
}: {
  name: string;
  segmentsCount: number;
  smallInputs?: boolean;
  hasFixedPenalty?: boolean;
}) {
  const { watch, setValue } = useFormContext();
  const config: ScoringConfig = watch(name);

  let rows = Math.max(
    1,
    config.successPoints.length || 0,
    config.segment1Points?.length || 0,
    config.segment2Points?.length || 0,
  );

  if (hasFixedPenalty) {
    rows = 1;
  }

  const canChangeRows = !hasFixedPenalty;

  const onAddRow = () => {
    const newRows = rows + 1;

    setValue(name, {
      successPoints: fillToLength(config.successPoints, newRows),
      segment1Points: fillToLength(config.segment1Points || [], newRows),
      segment2Points: fillToLength(config.segment2Points || [], newRows),
    });
  };

  const onRemoveRow = () => {
    const newRows = Math.max(1, rows - 1);
    setValue(name, {
      successPoints: config.successPoints.slice(0, newRows),
      segment1Points: fillToLength(config.segment1Points || [], rows)?.slice(0, newRows),
      segment2Points: fillToLength(config.segment2Points || [], rows)?.slice(0, newRows),
    });
  };

  const displayModal = useDisplayGenericModal();
  const onHelp = () => {
    displayModal({
      title: "Custom scoring help",
      body: (
        <Typography textAlign="left">
          {hasFixedPenalty ? (
            <>
              <p>
                Use the table to input the base top and (if applicable) zone points. The points
                scored by a user are then determined by:
              </p>
              <p>score = base points - (failed attempts × penalty)</p>
            </>
          ) : (
            <>
              <p>
                Use this table to set the score awarded based on the number of attempts and highest
                hold reached.{" "}
              </p>
              <p>
                You can add and remove rows to specify the score for each number of attempts.
                Filling in the value for e.g. “2+ tries” means that the points apply to tops/zones
                on the 2nd or higher attempt.
              </p>
            </>
          )}
        </Typography>
      ),
    });
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Table
        sx={{
          width: "auto",
          "tbody tr th": {
            textAlign: "right",
            borderBottom: "1px solid",
            borderColor: "var(--TableCell-borderColor)",
          },
          "tbody td": {
            verticalAlign: "top",
          },
          ...(smallInputs
            ? {
                tableLayout: "auto",
                width: "100%",
                input: { width: 0, minWidth: 30 },
              }
            : {}),
        }}
      >
        <thead>
          <tr>
            <th>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "flex-end",
                }}
              >
                <IconButton size="sm" sx={{ padding: 0, minWidth: 0, minHeight: 0 }}>
                  <Help fontSize="small" onClick={onHelp} />
                </IconButton>
              </Box>
            </th>
            <th>Top</th>
            {segmentsCount >= 1 && <th>Zone 1</th>}
            {segmentsCount >= 2 && <th>Zone 2</th>}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, i) => (
            <tr key={i}>
              <th>
                {i + 1}
                {!hasFixedPenalty && i === rows - 1 ? "+" : ""} {i === 0 ? "try" : "tries"}
              </th>
              <td>
                <NumberInput name={`${name}.successPoints.${i}`} label={null} decimalScale={3} />
              </td>
              {segmentsCount >= 1 && (
                <td>
                  <NumberInput name={`${name}.segment1Points.${i}`} label={null} decimalScale={3} />
                </td>
              )}
              {segmentsCount >= 2 && (
                <td>
                  <NumberInput name={`${name}.segment2Points.${i}`} label={null} decimalScale={3} />
                </td>
              )}
            </tr>
          ))}
          {canChangeRows ? (
            <tr>
              <td colSpan={4} style={{ textAlign: "center" }}>
                <Button
                  size="sm"
                  variant="soft"
                  startDecorator={<Add fontSize="small" />}
                  sx={{ ...compactButtonSx, marginRight: 1 }}
                  onClick={onAddRow}
                >
                  Add row
                </Button>
                <Button
                  size="sm"
                  variant="soft"
                  startDecorator={<Remove fontSize="small" />}
                  sx={compactButtonSx}
                  color="danger"
                  onClick={onRemoveRow}
                  disabled={rows <= 1}
                >
                  Remove row
                </Button>
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </Box>
  );
}
